import { computed, Injectable, signal } from '@angular/core';
import type {
  ModelUploadStatusItem,
  ModelUploadStatusPayload,
} from '../host-communication/messages/model-upload-status/interfaces/model-upload-status-payload.interface';

/**
 * Runtime-only store for model upload status updates received from the host.
 * Tracks items per project; nothing is persisted to localStorage.
 */
@Injectable({
  providedIn: 'root',
})
export class UploadStatusRuntimeStoreService {
  /** Internal signal holding all items keyed by `projectId::id`. */
  private readonly itemsMap = signal<Map<string, ModelUploadStatusItem>>(new Map());

  /** All tracked items as a flat array. */
  readonly items = computed(() => [...this.itemsMap().values()]);

  /** Returns a computed signal of items for a specific project. */
  itemsForProject(projectId: string) {
    return computed(() => this.items().filter(item => item.projectId === projectId));
  }

  /**
   * Processes a batch of status updates from the host.
   * - `Removed` items are deleted from the map.
   * - All other statuses are added or updated.
   */
  processStatusUpdate(payload: ModelUploadStatusPayload): void {
    this.itemsMap.update(current => {
      const next = new Map(current);
      for (const item of payload.items) {
        const key = `${item.projectId}::${item.id}`;
        if (item.status === 'Removed') {
          next.delete(key);
        } else {
          next.set(key, item);
        }
      }
      return next;
    });
  }

  /**
   * Applies a live percentage update sourced from SignalR import-progress events.
   * Only applies to items whose modelId matches the given modelId (SignalR's divisionid)
   * and whose status is Finalizing or Uploaded — the only phases where modelId is known
   * and the update is authoritative for. No-op for items that are not currently tracked.
   */
  applySignalrProgress(modelId: string, percentage: number): void {
    this.itemsMap.update(current => {
      let changed = false;
      const next = new Map(current);
      for (const [key, item] of next) {
        if (
          (item.modelId === modelId || item.id === modelId) &&
          (item.status === 'Finalizing' || item.status === 'Uploaded')
        ) {
          next.set(key, { ...item, percentage });
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }

  /**
   * Enriches an existing item with its creation date fetched from the API.
   * No-op if the item does not exist in the map.
   */
  enrichItem(projectId: string, modelId: string, creationDate: string): void {
    const key = `${projectId}::${modelId}`;
    this.itemsMap.update(current => {
      if (!current.has(key)) return current;
      const next = new Map(current);
      next.set(key, { ...next.get(key)!, creationDate });
      return next;
    });
  }

  /** Clears all tracked items. */
  clear(): void {
    this.itemsMap.set(new Map());
  }

  /** Clears only the tracked items belonging to the given project. */
  clearForProject(projectId: string): void {
    this.itemsMap.update(current => {
      const next = new Map(current);
      for (const [key, item] of next) {
        if (item.projectId === projectId) {
          next.delete(key);
        }
      }
      return next;
    });
  }
}
